<template>
  <div class="export-field-mapping">
    <v-card flat class="mapping-container">
      <!-- Header with column count -->
      <v-card-subtitle class="d-flex align-center justify-space-between py-2 px-0">
        <div class="d-flex align-center">
          <v-icon small color="primary" class="mr-2">mdi-table-column</v-icon>
          <span class="text-body-2 font-weight-medium">
            {{ includedColumnsCount }} of {{ columns.length }} columns selected
          </span>
        </div>
        <div class="d-flex align-center" style="gap: 8px">
          <v-btn text color="primary" @click="selectAll">
            <v-icon left>mdi-checkbox-multiple-marked</v-icon>
            Select All
          </v-btn>
          <v-btn text color="grey" @click="deselectAll">
            <v-icon left>mdi-checkbox-multiple-blank-outline</v-icon>
            Deselect All
          </v-btn>
        </div>
      </v-card-subtitle>

      <!-- Column mapping list -->
      <v-card outlined class="column-list-card">
        <v-list dense class="column-list py-0">
          <draggable
            v-model="localColumns"
            handle=".drag-handle"
            animation="200"
            ghost-class="ghost-item"
            @end="onDragEnd">
            <transition-group type="transition" name="column-list">
              <v-list-item
                v-for="(column, index) in localColumns"
                :key="column.name"
                class="column-item"
                :class="{ 'excluded-column': !column.include }">
                <!-- Drag handle -->
                <v-list-item-icon class="drag-handle mr-2" style="height: -webkit-fill-available;">
                  <v-icon small color="grey lighten-1">mdi-drag-vertical</v-icon>
                </v-list-item-icon>

                <!-- Include checkbox -->
                <v-list-item-action class="mr-3 my-0">
                  <v-checkbox
                    v-model="column.include"
                    dense
                    hide-details
                    color="primary"
                    @change="emitUpdate" />
                </v-list-item-action>

                <!-- Column info -->
                <v-list-item-content class="py-2">
                  <div class="d-flex align-center justify-space-between">
                    <div class="column-info">
                      <v-chip
                        x-small
                        :color="column.include ? 'primary' : 'grey'"
                        :outlined="!column.include"
                        class="order-chip mr-2">
                        {{ index + 1 }}
                      </v-chip>
                      <span class="original-name text-body-2">
                        {{ column.displayName }}
                      </span>
                      <v-chip
                        v-if="column.dataType"
                        x-small
                        outlined
                        class="ml-2 type-chip">
                        {{ column.dataType }}
                      </v-chip>
                    </div>

                    <!-- Target name input -->
                    <v-text-field
                      v-if="column.include"
                      v-model="column.targetName"
                      :placeholder="column.displayName"
                      dense
                      outlined
                      hide-details
                      class="target-name-input"
                      @input="emitUpdate">
                      <template v-slot:prepend-inner>
                        <v-icon small color="grey">mdi-arrow-right</v-icon>
                      </template>
                    </v-text-field>
                    <span v-else class="text-caption grey--text text--lighten-1 font-italic">
                      Excluded from export
                    </span>
                  </div>
                </v-list-item-content>
              </v-list-item>
            </transition-group>
          </draggable>
        </v-list>
      </v-card>

      <!-- Mapping summary -->
      <v-alert
        v-if="hasCustomMappings"
        dense
        outlined
        type="info"
        class="mt-3 mb-0">
        <div class="text-caption">
          <strong>Field Mapping Active:</strong> {{ customMappingCount }} column(s) will be renamed in the export file.
        </div>
      </v-alert>
    </v-card>
  </div>
</template>

<script>
import draggable from 'vuedraggable';

export default {
  name: 'ExportFieldMapping',
  components: {
    draggable,
  },
  props: {
    columns: {
      type: Array,
      required: true,
      default: () => [],
    },
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      localColumns: [],
    };
  },
  computed: {
    includedColumnsCount() {
      return this.localColumns.filter(c => c.include).length;
    },
    hasCustomMappings() {
      return this.localColumns.some(c => c.include && c.targetName && c.targetName !== c.displayName);
    },
    customMappingCount() {
      return this.localColumns.filter(c => c.include && c.targetName && c.targetName !== c.displayName).length;
    },
  },
  watch: {
    columns: {
      immediate: true,
      handler(newColumns) {
        this.initializeColumns(newColumns);
      },
    },
    value: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (newValue && Object.keys(newValue).length > 0) {
          this.applyExistingMapping(newValue);
        }
      },
    },
  },
  methods: {
    initializeColumns(columns) {
      // Initialize local columns with include flag and target name
      this.localColumns = columns.map((col, index) => ({
        name: col.name || col.Name,
        displayName: col.displayName || col.DisplayName || col.name || col.Name,
        dataType: col.dataType || col.DataType,
        include: true,
        targetName: '',
        order: col.order ?? index,
      }));
      this.localColumns.sort((a, b) => a.order - b.order);
    },
    applyExistingMapping(mapping) {
      if (!mapping.columns) return;

      this.localColumns.forEach(col => {
        const config = mapping.columns[col.name];
        if (config) {
          col.include = config.include ?? true;
          col.targetName = config.targetName || '';
          col.order = config.order ?? col.order;
        }
      });
      this.localColumns.sort((a, b) => a.order - b.order);
    },
    selectAll() {
      this.localColumns.forEach(col => {
        col.include = true;
      });
      this.emitUpdate();
    },
    deselectAll() {
      this.localColumns.forEach(col => {
        col.include = false;
      });
      this.emitUpdate();
    },
    onDragEnd() {
      // Update order after drag
      this.localColumns.forEach((col, index) => {
        col.order = index;
      });
      this.emitUpdate();
    },
    emitUpdate() {
      const mapping = {
        columns: {},
      };

      this.localColumns.forEach((col, index) => {
        mapping.columns[col.name] = {
          targetName: col.targetName || col.displayName,
          include: col.include,
          order: index,
        };
      });

      this.$emit('input', mapping);
      this.$emit('change', mapping);
    },
    getFieldMapping() {
      const mapping = {
        columns: {},
      };

      this.localColumns.forEach((col, index) => {
        mapping.columns[col.name] = {
          targetName: col.targetName || col.displayName,
          include: col.include,
          order: index,
        };
      });

      return mapping;
    },
  },
};
</script>

<style scoped>
/* ============================================
   EXPORT FIELD MAPPING - DESIGN SYSTEM COMPLIANT
   Supports both light and dark themes
   ============================================ */

.export-field-mapping {
  width: 100%;

  /* Local theme-aware variables */
  --efm-border-color: var(--ds-border-subtle);
  --efm-hover-bg: rgba(3, 98, 76, 0.06);
  --efm-excluded-bg: var(--ds-surface-hover);
  --efm-ghost-bg: rgba(3, 98, 76, 0.1);
  --efm-scrollbar: rgba(0, 0, 0, 0.15);
  --efm-scrollbar-hover: rgba(0, 0, 0, 0.25);
}

/* Dark theme overrides */
.theme--dark .export-field-mapping {
  --efm-hover-bg: rgba(3, 98, 76, 0.1);
  --efm-excluded-bg: rgba(255, 255, 255, 0.03);
  --efm-ghost-bg: rgba(3, 98, 76, 0.15);
  --efm-scrollbar: rgba(255, 255, 255, 0.15);
  --efm-scrollbar-hover: rgba(255, 255, 255, 0.25);
}

.mapping-container {
  background: transparent;
}

.column-list-card {
  border-radius: var(--ds-radius-md);
  overflow: hidden;
  max-height: 400px;
  overflow-y: auto;
  border-color: var(--efm-border-color) !important;
}

.column-list {
  background: transparent;
}

.column-item {
  border-bottom: 1px solid var(--efm-border-color);
  transition: all var(--ds-transition-default);
  min-height: 56px;
}

.column-item:last-child {
  border-bottom: none;
}

.column-item:hover {
  background-color: var(--efm-hover-bg);
}

.column-item.excluded-column {
  background-color: var(--efm-excluded-bg);
  opacity: 0.7;
}

.drag-handle {
  cursor: grab;
  min-width: 24px !important;
}

.drag-handle:active {
  cursor: grabbing;
}

.column-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.original-name {
  font-weight: 500;
  color: var(--v-text-base);
}

.order-chip {
  font-size: var(--ds-font-micro);
  height: 18px !important;
  min-width: 18px;
}

.type-chip {
  font-size: 9px;
  height: 16px !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.target-name-input {
  max-width: 220px;
  flex-shrink: 0;
}

.target-name-input :deep(.v-input__slot) {
  min-height: 32px !important;
  padding: 0 var(--ds-space-sm) !important;
}

.target-name-input :deep(.v-text-field__slot input) {
  font-size: var(--ds-font-body-sm);
}

/* Drag and drop styles */
.ghost-item {
  opacity: 0.5;
  background: var(--efm-ghost-bg);
}

.column-list-move {
  transition: transform var(--ds-transition-slow);
}

.column-list-enter-active,
.column-list-leave-active {
  transition: all var(--ds-transition-slow);
}

.column-list-enter,
.column-list-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

/* Scrollbar styling */
.column-list-card::-webkit-scrollbar {
  width: 6px;
}

.column-list-card::-webkit-scrollbar-track {
  background: transparent;
}

.column-list-card::-webkit-scrollbar-thumb {
  background-color: var(--efm-scrollbar);
  border-radius: 3px;
}

.column-list-card::-webkit-scrollbar-thumb:hover {
  background-color: var(--efm-scrollbar-hover);
}
</style>
